import { integrationsApi } from '@/lib/api/client'
import { QUERY_KEYS } from '@/lib/api/query-keys'
import {
  listDtoolsSiImportAttempts,
  removeDtoolsSiImportAttempt,
} from '@/lib/dtools-si-import-attempt'
import {
  DtoolsSiImportFailedError,
  DtoolsSiImportStatusUnresolvedError,
  recoverDtoolsSiImportAttempt,
} from '@/lib/dtools-si-import-recovery'
import { toast } from '@/lib/hooks/use-toast'
import { trackException } from '@/lib/telemetry'
import { useQueryClient } from '@tanstack/react-query'
import { memo, useEffect } from 'react'

export const DtoolsSiImportRecovery = memo(function DtoolsSiImportRecovery({ quoteId }: { quoteId: string }) {
  const queryClient = useQueryClient()

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    const recover = async () => {
      for (const attempt of listDtoolsSiImportAttempts(quoteId)) {
        try {
          const result = await recoverDtoolsSiImportAttempt(attempt, {
            requestStatus: async (pendingAttempt, timeoutMs) => {
              const response = await integrationsApi.get('/dtools-si/import-status', {
                params: {
                  quote_id: pendingAttempt.quoteId,
                  si_project_id: pendingAttempt.siProjectId,
                  import_attempt_id: pendingAttempt.attemptId,
                },
                timeout: timeoutMs,
                signal: controller.signal,
              })
              return response.data
            },
            signal: controller.signal,
          })
          if (cancelled) return
          removeDtoolsSiImportAttempt(attempt.quoteId, attempt.siProjectId, attempt.attemptId)
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quote() }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dtoolsSiQuoteActions(attempt.quoteId) }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailsRoot }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailSummaryRoot }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentMilestones }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accountRooms }),
          ])
          toast({
            title: 'D-Tools SI import completed',
            description:
              result.status === 'imported_ack_pending'
                ? 'Quote is imported. D-Tools acknowledgement is pending; do not import again.'
                : 'Import completed after the prior page session ended.',
            variant: 'success',
          })
        } catch (error) {
          if (cancelled) return
          if (error instanceof DtoolsSiImportFailedError) {
            removeDtoolsSiImportAttempt(attempt.quoteId, attempt.siProjectId, attempt.attemptId)
          }
          trackException(error, { tag: 'dtools_si_import_resume_failed', quote_id: quoteId })
          toast({
            title:
              error instanceof DtoolsSiImportStatusUnresolvedError
                ? 'D-Tools SI import still unresolved'
                : 'Unable to reconcile D-Tools SI import',
            error,
            variant: error instanceof DtoolsSiImportStatusUnresolvedError ? 'default' : 'destructive',
          })
        }
      }
    }
    void recover()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [queryClient, quoteId])

  return null
})
